import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { HEALTH_META, signalPath } from "./EntityHealth";

const STATUS_RANK = { at_risk: 0, needs_attention: 1, healthy: 2 };
const SEVERITY_RANK = { high: 0, medium: 1, low: 2, info: 3 };

// Surfaces the single riskiest entity + its top reason at the top of Group HQ.
export default function AttentionBanner({ entities, entityHealth }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("attention_dismissed") === "1"
  );

  // Pick the riskiest non-healthy entity (at_risk first, then by score).
  const candidates = entities
    .map((e) => ({ e, h: entityHealth[e.id] }))
    .filter((x) => x.h && x.h.status !== "healthy")
    .sort((a, b) => {
      const s = (STATUS_RANK[a.h.status] ?? 3) - (STATUS_RANK[b.h.status] ?? 3);
      return s !== 0 ? s : (b.h.score || 0) - (a.h.score || 0);
    });

  if (dismissed || !candidates.length) return null;

  const { e, h } = candidates[0];
  const meta = HEALTH_META[h.status];
  // Top reason = highest-severity scored signal; tiebreak by count desc (ignore info-only).
  const topSignal = [...h.signals]
    .filter((s) => s.severity !== "info")
    .sort((a, b) => {
      const sev = (SEVERITY_RANK[a.severity] ?? 4) - (SEVERITY_RANK[b.severity] ?? 4);
      return sev !== 0 ? sev : (b.count || 0) - (a.count || 0);
    })[0]
    || h.signals[0];
  const reason = topSignal ? t(`health_sig_${topSignal.key}`) : "";
  const target = topSignal ? signalPath(topSignal.key, e.key) : `/entity/${e.key}`;

  const dismiss = () => {
    sessionStorage.setItem("attention_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      className="relative mb-6 rounded-2xl border p-4 sm:p-5 flex items-center gap-4 overflow-hidden"
      style={{ borderColor: `${meta.color}66`, background: `${meta.color}12` }}
      data-testid="attention-banner"
    >
      <span
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: meta.color }}
      />
      <span
        className="hidden sm:flex w-10 h-10 rounded-xl items-center justify-center shrink-0"
        style={{ backgroundColor: `${meta.color}22` }}
      >
        <AlertTriangle className="w-5 h-5" style={{ color: meta.color }} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold tracking-wide uppercase" style={{ color: meta.color }}>
          {t("attention_today")}
        </div>
        <div className="text-sm sm:text-base text-text-primary mt-0.5">
          <button
            onClick={() => navigate(`/entity/${e.key}`)}
            className="font-semibold hover:underline"
            data-testid="attention-entity"
          >
            {e.name}
          </button>{" "}
          <span className="text-text-secondary">
            {t(`attention_is_${h.status}`)}{reason ? ` — ${reason.toLowerCase()}` : ""}
            {topSignal && topSignal.count > 1 ? ` (${topSignal.count})` : ""}.
          </span>
        </div>
      </div>
      <button
        onClick={() => navigate(target)}
        data-testid="attention-cta"
        className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium text-white shrink-0 transition-transform hover:scale-[1.03]"
        style={{ backgroundColor: meta.color }}
      >
        {t("attention_view")} <ArrowRight className="w-4 h-4" />
      </button>
      <button
        onClick={dismiss}
        data-testid="attention-dismiss"
        className="text-text-secondary hover:text-text-primary transition-colors shrink-0"
        aria-label={t("dismiss")}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
